import React, { useState } from "react";
import toast from "react-hot-toast";
import {
  useAllCategoriesQuery,
  useDisableProductMutation,
  useEnableProductMutation,
  useInventoryQuery,
} from "../../app/apiSlices/dbSlice";

const Inventory = () => {
  const { data, isLoading, refetch } = useInventoryQuery();
  const { data: categories } = useAllCategoriesQuery();

  const [tableData, setTableData] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  
  const [enable, { isLoading: isEnableLoading }] = useEnableProductMutation();
  const [disable, { isLoading: isDisableLoading }] =
    useDisableProductMutation();

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCategory(e.target.value);
  };

  React.useEffect(() => {
    if (selectedCategory === "") {
      setTableData(data?.data);
    } else {
      const filteredData = data?.data.filter(
        (item: InventoryItem) => item.category.name === selectedCategory
      );
      setTableData(filteredData);
    }
  }, [selectedCategory, data]);

  const handleAvailability = async (item: InventoryItem) => {
    if (isEnableLoading || isDisableLoading)
      return toast.error("Please wait...");
    //check if item is available
    if (item.in_stock) {
      //disable
      toast.loading("Removing from stock...", { id: "remove" });
      try {
        await disable(item.id).unwrap();
        refetch();
        toast.success("Item removed from stock", { id: "remove" });
      } catch (error: any) {
        toast.error(error.message ?? "An error occured");
      }
    } else {
      //enable
      toast.loading("Adding to stock...", { id: "add" });
      try {
        await enable(item.id).unwrap();
        refetch();
        toast.success("Item added to stock", { id: "add" });
      } catch (error: any) {
        toast.error(error.message ?? "An error occured");
      }
    }
  };

  return (
    <div className="px-8">
      <div className="w-full flex items-center justify-between">
        <h3>Product List</h3>

        <div className="flex items-center gap-2">
          <p>Filter by Category</p>
          <select
            name=""
            id=""
            className="border p-2 rounded"
            onChange={handleCategoryChange}
          >
            <option value="">All</option>
            {categories?.data?.map(
              (category: any, index: React.Key | null | undefined) => (
                <option key={index} value={category.name}>
                  {category.name}
                </option>
              )
            )}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto w-full mt-10">
        <table className="w-full bg-white rounded-lg">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="p-4 text-sm font-medium text-left">
                Product ID(#)
              </th>
              <th className="p-4 text-sm font-medium text-left">Product</th>
              <th className="p-4 text-sm font-medium text-left">Category</th>
              <th className="p-4 text-sm font-medium text-left">Updated At</th>
              <th className="p-4 text-sm font-medium text-left">
                Amount(/pack)
              </th>
              <th className="p-4 text-sm font-medium text-left min-w-[50px]">
                In stock
              </th>
            </tr>
          </thead>

          <tbody>
            {tableData?.map(
              (product: InventoryItem, index: React.Key | null | undefined) => (
                <tr
                  className="border-b border-[#F2F2F2] text-[#71747D]"
                  key={index}
                >
                  <td className="px-4 py-4 text-sm min-w-[50px]">
                    {product.id}
                  </td>
                  <td className="px-4 py-4 text-sm min-w-[200px]">
                    {product.name}
                  </td>
                  <td className="px-4 py-4 text-sm">{product.category.name}</td>
                  <td className="px-4 py-4 text-sm min-w-[100px]">
                    {product.updated_at.split("T")[0]}
                  </td>
                  <td className="px-4 py-4 text-sm">N {product.price}</td>
                  <td className="px-4 py-4 text-sm">
                    <input
                      type="checkbox"
                      name=""
                      id=""
                      checked={product.in_stock}
                      readOnly
                      onClick={() => handleAvailability(product)}
                      className="accent-[#2E4057] bg-[#2E4057]"
                    />
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
        {isLoading && (
          <div className="flex items-center justify-center py-10 w-full">
            <svg
              aria-hidden="true"
              className=" w-8 h-8 text-gray-200 animate-spin  fill-white"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};

export default Inventory;

export interface InventoryItem {
  id: number;
  description: string;
  name: string;
  price: string;
  in_stock: boolean;
  category: {
    id: number;
    name: string;
  };
  category_id: number;
  updated_at: string;
  created_at: string;
}
